import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { doc, getDoc, collection, addDoc, getDocs } from "firebase/firestore";
import { Button, Row, Col, Card, Form, ProgressBar, Container, Alert, FormControl } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Header from "../layouts/Header";

export default function FileManager() {
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [mediaLinks, setMediaLinks] = useState([]);
  const [mediaLink, setMediaLink] = useState("");
  const [mediaName, setMediaName] = useState(""); // Added media name
  const [mediaType, setMediaType] = useState("photo"); // photo or video
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [createdForEmail, setCreatedForEmail] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchUserData = async () => {
    const user = auth.currentUser;
    if (user) {
      setEmail(user.email || "No email");
      const userDocRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFullName(userData.fullName || "Unknown User");
        setRole(userData.role || "user");
      } else {
        console.error("User document does not exist");
      }
    }
  };

  const fetchAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const userList = usersSnapshot.docs.map((doc) => ({
        email: doc.data().email,
        fullName: doc.data().fullName || "Unknown User",
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchAllUsers();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return alert("Please select a file first.");
    if (!createdForEmail) return alert("Please enter the email of the user this file is for.");
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "documents");

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://api.cloudinary.com/v1_1/dwcbtliac/upload");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      };

      xhr.onload = async () => {
        const data = JSON.parse(xhr.responseText);
        const fileRef = collection(db, "Files");
        await addDoc(fileRef, {
          fileName: file.name,
          url: data.secure_url,
          size: file.size,
          type: file.type,
          uploadedAt: new Date(),
          createdBy: { email, fullName },
          createdFor: createdForEmail,
        });

        setUploading(false);
        setUploadProgress(0);
        setFile(null);
        setCreatedForEmail("");
        alert("File uploaded successfully!");
        fetchFiles();
      };

      xhr.onerror = () => {
        setUploading(false);
        setUploadProgress(0);
        alert("Failed to upload file.");
      };

      xhr.send(formData);
    } catch (err) {
      console.error(err);
      alert("Failed to upload file.");
    }
  };

  const handleMediaLinkAdd = async () => {
    if (!mediaLink || !mediaName || !createdForEmail) {
      alert("Please enter a valid link, name, and recipient's email.");
      return;
    }
    try {
      const mediaRef = collection(db, "MediaLinks");
      await addDoc(mediaRef, {
        link: mediaLink,
        name: mediaName,
        type: mediaType,
        createdAt: new Date(),
        createdBy: { email, fullName },
        createdFor: createdForEmail,
      });

      setMediaLink("");
      setMediaName("");
      setMediaType("photo");
      setCreatedForEmail("");
      alert("Media link added successfully!");
      fetchMediaLinks();
    } catch (err) {
      console.error(err);
      alert("Failed to add media link.");
    }
  };

  const fetchFiles = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const fileSnapshot = await getDocs(collection(db, "Files"));
      const allFiles = fileSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      let filteredFiles = [];
      if (role === "admin") {
        filteredFiles = selectedUser
          ? allFiles.filter(
              (file) =>
                file.createdBy.email === selectedUser.email ||
                file.createdFor === selectedUser.email
            )
          : allFiles;
      } else {
        filteredFiles = allFiles.filter(
          (file) => file.createdBy.email === email || file.createdFor === email
        );
      }

      setFiles(filteredFiles);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch files.");
    }
  };

  const fetchMediaLinks = async () => {
    try {
      const mediaSnapshot = await getDocs(collection(db, "MediaLinks"));
      const allMediaLinks = mediaSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      let filteredMediaLinks = [];
      if (role === "admin") {
        filteredMediaLinks = selectedUser
          ? allMediaLinks.filter(
              (media) =>
                media.createdBy.email === selectedUser.email ||
                media.createdFor === selectedUser.email
            )
          : allMediaLinks;
      } else {
        filteredMediaLinks = allMediaLinks.filter(
          (media) => media.createdBy.email === email || media.createdFor === email
        );
      }
      setMediaLinks(filteredMediaLinks);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch media links.");
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchMediaLinks();
  }, [role, selectedUser]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowerCaseQuery = query.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(lowerCaseQuery) ||
        user.email.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  };

  /**
   * Extracts an embeddable video link from a given URL (e.g., YouTube, Vimeo).
   */
  const getEmbeddableVideoLink = (url) => {
    const youtubeMatch = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    const vimeoMatch = url.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/);

    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    return null;
  };

  return (
    <React.Fragment>
      <Header />
      <Container fluid className="main main-file-manager p-4">
        <Alert variant="info" className="mb-4">
          {role === "admin"
            ? "You are logged in as an admin and can view all files and media for all users."
            : `You are logged in as a ${role}. You can view files and media created by you or for you.`}
        </Alert>
        <Alert variant="warning" className="mb-4">
          For media (photos or videos), it is advised to provide direct links instead of uploading files.
        </Alert>

        {role === "admin" && (
          <Card className="filter-card mb-4 shadow-sm">
            <Card.Body>
              <h5 className="mb-3 text-primary">Filter Files by User</h5>
              <FormControl
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="mb-3"
              />
              <PerfectScrollbar style={{ maxHeight: "200px", overflow: "auto" }}>
                <ul className="list-group">
                  {filteredUsers.map((user, index) => (
                    <li
                      key={index}
                      className={`list-group-item ${selectedUser === user ? "active" : ""}`}
                      onClick={() => setSelectedUser(user)}
                      style={{ cursor: "pointer" }}
                    >
                      {user.fullName} ({user.email})
                    </li>
                  ))}
                </ul>
              </PerfectScrollbar>
            </Card.Body>
          </Card>
        )}

        <Row className="mb-4">
          <Col md="6">
            <Card className="upload-card shadow-sm">
              <Card.Body>
                <h4 className="mb-3 text-primary">Upload File</h4>
                <Form.Group controlId="fileUpload">
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    className="mb-2"
                    placeholder="Select a file"
                  />
                  <Form.Control
                    type="email"
                    value={createdForEmail}
                    onChange={(e) => setCreatedForEmail(e.target.value)}
                    placeholder="Enter recipient's email"
                    className="mb-3"
                  />
                  <Button variant="primary" onClick={handleUpload} disabled={uploading}>
                    {uploading ? "Uploading..." : "Upload"}
                  </Button>
                  {uploading && (
                    <ProgressBar
                      animated
                      now={uploadProgress}
                      label={`${uploadProgress}%`}
                      className="mt-3"
                    />
                  )}
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6">
            <Card className="upload-media-card shadow-sm">
              <Card.Body>
                <h4 className="mb-3 text-primary">Add Media Link</h4>
                <Form.Group controlId="mediaLinkUpload">
                  <Form.Control
                    type="text"
                    value={mediaName}
                    onChange={(e) => setMediaName(e.target.value)}
                    placeholder="Enter media name"
                    className="mb-2"
                  />
                  <Form.Control
                    type="text"
                    value={mediaLink}
                    onChange={(e) => setMediaLink(e.target.value)}
                    placeholder="Enter photo or video link"
                    className="mb-2"
                  />
                  <Form.Select
                    value={mediaType}
                    onChange={(e) => setMediaType(e.target.value)}
                    className="mb-2"
                  >
                    <option value="photo">Photo</option>
                    <option value="video">Video</option>
                  </Form.Select>
                  <Form.Control
                    type="email"
                    value={createdForEmail}
                    onChange={(e) => setCreatedForEmail(e.target.value)}
                    placeholder="Enter recipient's email"
                    className="mb-3"
                  />
                  <Button variant="primary" onClick={handleMediaLinkAdd}>
                    Add Media Link
                  </Button>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <h4 className="mb-4 text-primary">Uploaded Files</h4>
        <PerfectScrollbar>
          <Row className="g-4">
            {files.map((file) => (
              <Col xs="12" sm="6" md="4" lg="3" key={file.id}>
                <Card className="file-card shadow-sm">
                  <Card.Body>
                    <Card.Title className="text-truncate text-dark">{file.fileName}</Card.Title>
                    <Card.Text>
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-primary"
                      >
                        View File
                      </a>
                    </Card.Text>
                    <Card.Text className="text-muted small">
                      Size: {(file.size / 1024).toFixed(2)} KB
                    </Card.Text>
                    <Card.Text className="text-muted small">
                      Created by: {file.createdBy.fullName || "Unknown"} ({file.createdBy.email})
                    </Card.Text>
                    <Card.Text className="text-muted small">
                      Created for: {file.createdFor}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </PerfectScrollbar>

        <h4 className="mb-4 mt-4 text-primary">Uploaded Media</h4>
        <PerfectScrollbar>
          <Row className="g-4">
            {mediaLinks.map((media) => (
              <Col xs="12" sm="6" md="4" lg="3" key={media.id}>
                <Card className="media-card shadow-sm">
                  <Card.Body>
                    <Card.Title className="text-truncate text-dark">{media.name}</Card.Title>
                    {media.type === "photo" && (
                      <Card.Img
                        variant="top"
                        src={media.link}
                        alt={media.name}
                        className="mb-3"
                      />
                    )}
                    {media.type === "video" && (
                      <iframe
                        width="100%"
                        height="150"
                        src={getEmbeddableVideoLink(media.link)}
                        title={media.name}
                        frameBorder="0"
                        allowFullScreen
                        className="mb-3"
                      />
                    )}
                    {!getEmbeddableVideoLink(media.link) && (
                      <Card.Text>
                        <a
                          href={media.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          View Video
                        </a>
                      </Card.Text>
                    )}
                    <Card.Text className="text-muted small">
                      Created by: {media.createdBy.fullName || "Unknown"} ({media.createdBy.email})
                    </Card.Text>
                    <Card.Text className="text-muted small">
                      Created for: {media.createdFor}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </PerfectScrollbar>
      </Container>
    </React.Fragment>
  );
}
